import { ArrowDownCircle, ArrowLeft, ArrowUpCircle, PiggyBank, Users, Wallet } from 'lucide-react';
import { useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { useActiveGroup } from '@/features/groups/store';
import { useBalances, useLedgerStore, useMemberLookup } from '@/features/ledger/store';
import { formatDate, formatRelativeDate } from '@/lib/date';
import {
  Avatar,
  Badge,
  Button,
  Card,
  CardHeader,
  CategoryGlyph,
  DataTable,
  EmptyState,
  Money,
  PageHeader,
  StatTile,
  balancePhrase,
  type Column,
} from '@/ui';
import { ledgerFor } from '@domain/balance';
import { formatMoney } from '@domain/money';
import type { ExpenseWithDetails, Settlement } from '@domain/types';

type Involvement = {
  expense: ExpenseWithDetails;
  shareMinor: number;
  paidMinor: number;
};

export function MemberLedgerPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const group = useActiveGroup();
  const expenses = useLedgerStore((state) => state.allExpenses);
  const participants = useLedgerStore((state) => state.participants);
  const settlements = useLedgerStore((state) => state.settlements);
  const balances = useBalances();
  const memberLookup = useMemberLookup();

  const currency = group?.currency ?? 'USD';
  const member = id ? memberLookup.get(id) : undefined;
  const balance = balances.find((candidate) => candidate.memberId === id);

  const { owes, owedBy } = useMemo(
    () => (id ? ledgerFor(id, balances, expenses, participants, settlements) : { owes: [], owedBy: [] }),
    [id, balances, expenses, participants, settlements]
  );

  const involvement = useMemo<Involvement[]>(() => {
    if (!id) return [];
    return expenses.flatMap((expense) => {
      const share = expense.participants.find(
        (participant) => participant.memberId === id
      )?.shareAmountMinor;
      const paid = expense.paidBy === id ? expense.amountMinor : 0;
      if (share === undefined && paid === 0) return [];
      return [{ expense, shareMinor: share ?? 0, paidMinor: paid }];
    });
  }, [expenses, id]);

  const memberSettlements = useMemo(
    () =>
      settlements.filter(
        (settlement) => settlement.fromMemberId === id || settlement.toMemberId === id
      ),
    [settlements, id]
  );

  if (!member || !balance) {
    return (
      <>
        <PageHeader title="Member" />
        <Card padded={false}>
          <EmptyState
            icon={Users}
            title="Member not found"
            description="They may have been removed from this group."
            action={<Button onClick={() => navigate('/members')}>Back to members</Button>}
          />
        </Card>
      </>
    );
  }

  const expenseColumns: Column<Involvement>[] = [
    {
      key: 'title',
      header: 'Expense',
      cell: ({ expense }) => (
        <span className="flex items-center gap-2.5">
          <CategoryGlyph icon={expense.categoryIcon} color={expense.categoryColor} size="sm" />
          <span className="min-w-0">
            <span className="block truncate font-medium">{expense.title}</span>
            <span className="block text-[12px] text-muted">
              {formatRelativeDate(expense.date)}
            </span>
          </span>
        </span>
      ),
    },
    {
      key: 'paid',
      header: 'They paid',
      align: 'right',
      hideOnNarrow: true,
      cell: ({ paidMinor }) =>
        paidMinor > 0 ? (
          <Money minor={paidMinor} currency={currency} />
        ) : (
          <span className="text-muted">—</span>
        ),
    },
    {
      key: 'total',
      header: 'Expense total',
      align: 'right',
      hideOnNarrow: true,
      cell: ({ expense }) => <Money minor={expense.amountMinor} currency={currency} muted />,
    },
    {
      key: 'share',
      header: 'Their share',
      align: 'right',
      width: '140px',
      cell: ({ shareMinor }) => (
        <Money minor={shareMinor} currency={currency} className="font-semibold" />
      ),
    },
  ];

  const settlementColumns: Column<Settlement>[] = [
    {
      key: 'direction',
      header: 'Payment',
      cell: (row) => {
        const outgoing = row.fromMemberId === member.id;
        const other = memberLookup.get(outgoing ? row.toMemberId : row.fromMemberId);
        const Icon = outgoing ? ArrowUpCircle : ArrowDownCircle;

        return (
          <span className="flex items-center gap-2.5">
            <Icon size={17} className={outgoing ? 'text-danger' : 'text-success'} />
            <span className="truncate">
              {outgoing ? 'Paid' : 'Received from'} {other?.name ?? 'Unknown'}
            </span>
          </span>
        );
      },
    },
    {
      key: 'date',
      header: 'Date',
      align: 'right',
      hideOnNarrow: true,
      cell: (row) => <span className="text-muted">{formatDate(row.date)}</span>,
    },
    {
      key: 'note',
      header: 'Note',
      hideOnNarrow: true,
      cell: (row) => <span className="text-muted">{row.note ?? '—'}</span>,
    },
    {
      key: 'amount',
      header: 'Amount',
      align: 'right',
      width: '140px',
      cell: (row) => (
        <Money
          minor={row.amountMinor}
          currency={currency}
          className={row.fromMemberId === member.id ? 'text-danger' : 'text-success'}
        />
      ),
    },
  ];

  return (
    <>
      <Link
        to="/members"
        className="mb-4 inline-flex items-center gap-1.5 text-[13px] text-muted transition hover:text-fg"
      >
        <ArrowLeft size={14} /> All members
      </Link>

      <PageHeader
        title={member.name}
        description={
          <span className="flex items-center gap-2">
            {balancePhrase(balance.netMinor, currency)}
            {!member.isActive && <Badge>Inactive</Badge>}
          </span>
        }
        actions={<Button variant="secondary" onClick={() => navigate('/settle')}>Settle up</Button>}
      />

      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        <div className="col-span-2 rounded-card border border-line bg-surface p-5 shadow-card">
          <div className="flex items-center gap-3">
            <Avatar name={member.name} id={member.id} size="lg" dimmed={!member.isActive} />
            <div className="min-w-0">
              <p className="text-[12px] font-medium uppercase tracking-wide text-muted">
                Net balance
              </p>
              <Money
                minor={balance.netMinor}
                currency={currency}
                signed
                className="text-3xl font-semibold"
              />
              {member.phone && <p className="text-[12px] text-muted">{member.phone}</p>}
            </div>
          </div>
        </div>

        <StatTile
          label="Paid"
          icon={Wallet}
          value={formatMoney(balance.paidMinor, currency)}
          caption="Money they put in"
        />
        <StatTile
          label="Their share"
          icon={PiggyBank}
          value={formatMoney(balance.owedMinor, currency)}
          caption="What they're responsible for"
        />
      </div>

      {(owes.length > 0 || owedBy.length > 0) && (
        <section className="mt-6">
          <CardHeader
            title="To settle up"
            description="From the group's simplified payment plan."
          />
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {owes.map((suggestion) => (
              <Card key={`owes-${suggestion.toMemberId}`} className="flex min-w-0 items-center gap-3">
                <ArrowUpCircle size={20} className="shrink-0 text-danger" />
                <span className="min-w-0 flex-1 truncate text-sm">
                  Pay {suggestion.toMemberName}
                </span>
                <Money
                  minor={suggestion.amountMinor}
                  currency={currency}
                  className="font-semibold text-danger"
                />
              </Card>
            ))}
            {owedBy.map((suggestion) => (
              <Card key={`owed-${suggestion.fromMemberId}`} className="flex min-w-0 items-center gap-3">
                <ArrowDownCircle size={20} className="shrink-0 text-success" />
                <span className="min-w-0 flex-1 truncate text-sm">
                  Collect from {suggestion.fromMemberName}
                </span>
                <Money
                  minor={suggestion.amountMinor}
                  currency={currency}
                  className="font-semibold text-success"
                />
              </Card>
            ))}
          </div>
        </section>
      )}

      <section className="mt-8">
        <CardHeader title={`Expenses (${involvement.length})`} />
        <DataTable
          columns={expenseColumns}
          rows={involvement}
          rowKey={({ expense }) => expense.id}
          onRowClick={({ expense }) => navigate(`/expenses/${expense.id}`)}
          empty={
            <EmptyState title="Not on any expense yet" description={`${member.name} hasn't paid for or shared an expense.`} />
          }
        />
      </section>

      <section className="mt-8">
        <CardHeader title={`Settlements (${memberSettlements.length})`} />
        <DataTable
          columns={settlementColumns}
          rows={memberSettlements}
          rowKey={(row) => row.id}
          empty={<EmptyState title="No payments recorded" description={`Nothing has been settled with ${member.name} yet.`} />}
        />
      </section>
    </>
  );
}
